import React, { useState } from 'react';
import { Bookmark, Plus, Check, Loader2, MessageSquare } from 'lucide-react';
import { useQuran } from '../context/QuranContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import LoginPromptDialog from './LoginPromptDialog';
import { toast } from 'sonner';

const AddToTopic = ({ verseKey, surahName }) => {
    const { topics, createTopic, addVerseToTopic } = useQuran();
    const { isAuthenticated } = useAuth();
    
    const [isOpen, setIsOpen] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [newTopicName, setNewTopicName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    // State for the currently selected topic in the dialog
    const [selectedTopicId, setSelectedTopicId] = useState(null);
    const [comment, setComment] = useState('');

    const handleCreateTopic = async () => {
        if (!newTopicName.trim()) return;
        setIsSaving(true);
        try {
            const newTopic = await createTopic(newTopicName.trim());
            if (newTopic) {
                setNewTopicName('');
                setIsCreating(false);
                setSelectedTopicId(newTopic.id);
                toast.success('Topic created');
            } else {
                toast.error('Failed to create topic');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveToTopic = async () => {
        if (!selectedTopicId) return;
        setIsSaving(true);
        try {
            const success = await addVerseToTopic(selectedTopicId, verseKey, comment.trim());
            if (success) {
                toast.success('Verse added to topic');
                setIsOpen(false);
                setComment('');
                setSelectedTopicId(null);
            } else {
                toast.error('Failed to add verse to topic');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenChange = (open) => {
        setIsOpen(open);
        if (!open) {
            setIsCreating(false);
            setNewTopicName('');
            setComment('');
            setSelectedTopicId(null);
        }
    };

    const handleButtonClick = (e) => {
        if (!isAuthenticated) {
            e.preventDefault();
            setShowLoginPrompt(true);
        }
    };

    return (
        <>
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#10b981] hover:text-[#34d399] hover:bg-[#10b981]/10 h-8 gap-2"
                    data-testid="add-to-topic-btn"
                    onClick={handleButtonClick}
                >
                    <Bookmark className="w-4 h-4" />
                    <span className="hidden sm:inline">Add to Topic</span>
                </Button>
            </DialogTrigger>

            <DialogContent className="bg-[#0f172a] border-[#1e293b] text-[#f8fafc] max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[#f8fafc]">
                        <Bookmark className="w-5 h-5 text-[#10b981]" />
                        Save Verse to Topic
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="p-3 bg-[#09090b]/50 rounded border border-[#1e293b]">
                        <p className="text-sm font-medium text-[#f8fafc]">
                            {surahName} {verseKey}
                        </p>
                    </div>

                    {!isCreating ? (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-[#94a3b8]">Select Topic</label>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 text-xs text-[#10b981] hover:text-[#34d399] p-0"
                                    onClick={() => setIsCreating(true)}
                                >
                                    <Plus className="w-3 h-3 mr-1" /> New Topic
                                </Button>
                            </div>
                            
                            {topics.length > 0 ? (
                                <ScrollArea className="h-40 border border-[#1e293b] rounded-md p-1">
                                    <div className="space-y-1">
                                        {topics.map(topic => (
                                            <button
                                                key={topic.id}
                                                onClick={() => setSelectedTopicId(topic.id)}
                                                className={`w-full text-left px-3 py-2 rounded-sm text-sm flex items-center justify-between transition-colors ${
                                                    selectedTopicId === topic.id 
                                                        ? 'bg-[#10b981]/20 text-[#10b981]' 
                                                        : 'hover:bg-[#1e293b] text-[#e2e8f0]'
                                                }`}
                                            >
                                                <span className="truncate pr-4">{topic.name}</span>
                                                {selectedTopicId === topic.id && <Check className="w-4 h-4 flex-shrink-0" />}
                                            </button>
                                        ))}
                                    </div>
                                </ScrollArea>
                            ) : (
                                <div className="text-center p-4 border border-[#1e293b] border-dashed rounded-md">
                                    <p className="text-sm text-[#94a3b8] mb-2">No topics created yet.</p>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => setIsCreating(true)}
                                        className="border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10"
                                    >
                                        Create your first topic
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3 p-3 bg-[#1e293b]/50 rounded-lg border border-[#334155]">
                            <label className="text-sm font-medium text-[#94a3b8]">New Topic Name</label>
                            <Input
                                value={newTopicName}
                                onChange={(e) => setNewTopicName(e.target.value)}
                                placeholder="e.g. Patience, Salaat, Stories of Prophets"
                                className="bg-[#09090b] border-[#334155] text-white"
                                autoFocus
                            />
                            <div className="flex gap-2 justify-end pt-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setIsCreating(false);
                                        setNewTopicName('');
                                    }}
                                    className="text-[#94a3b8]"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={handleCreateTopic}
                                    disabled={!newTopicName.trim() || isSaving}
                                    className="bg-[#10b981] hover:bg-[#059669] text-white"
                                >
                                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {selectedTopicId && !isCreating && (
                        <div className="space-y-2 pt-2 border-t border-[#1e293b]">
                            <label className="text-sm font-medium text-[#94a3b8] flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                Add a Note (Optional)
                            </label>
                            <Textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Why does this verse belong in this topic? This helps when reviewing the topic later to build a story."
                                className="bg-[#09090b] border-[#1e293b] text-[#f8fafc] text-sm resize-none"
                                rows={3}
                            />
                        </div>
                    )}

                    <div className="pt-4 flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setIsOpen(false)}
                            className="border-[#1e293b] text-[#94a3b8] hover:text-white"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveToTopic}
                            disabled={!selectedTopicId || isSaving || isCreating}
                            className="bg-[#10b981] hover:bg-[#059669] text-white"
                        >
                            {isSaving ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                            ) : (
                                'Save to Topic'
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>

        <LoginPromptDialog
            isOpen={showLoginPrompt}
            onClose={() => setShowLoginPrompt(false)}
            featureName="Add to Topic"
        />
        </>
    );
};

export default AddToTopic;
